import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatINR, type RentStatus } from '@new-wealth/shared';
import { api, ApiError } from '../api/client.js';
import { RentStatusBadge } from '../components/RentStatusBadge.js';
import { useAuth } from '../auth/AuthContext.js';
import { useConfirm } from '../components/Confirm.js';

/**
 * Locker tenants, branch-wise — every tenant, not only the ones NCD backs. The
 * roster is one call to LockerHub's /locker-tenants (all branches, or scoped to
 * one); NCD's own pledges and cheques are layered on top, and lockers of ours
 * not allotted yet are appended. The stock panel is their /locker-inventory
 * (A15) — every size including the sold-out ones, which the older
 * /locker-availability quote deliberately omits.
 *
 * Status shown is `account_status` (Active | Closure Requested) — the locker's
 * own status is always "occupied" here, since their query filters on it, and a
 * closed tenancy leaves the roster entirely rather than changing status.
 */
const card = 'bg-surface border border-border rounded-lg shadow-card p-5 mb-4';
const inp = 'px-2.5 py-1.5 text-sm border border-border-strong rounded outline-none focus:border-primary';

function Stat({ label, value, tone, hint }: { label: string; value: number | string; tone?: string; hint?: string }) {
  return (
    <div title={hint}>
      <div className={`text-lg font-semibold leading-tight ${tone ?? ''}`}>{value}</div>
      <div className="text-[11px] text-text-muted uppercase tracking-wide">{label}</div>
      {hint && <div className="text-[11px] text-text-muted">{hint}</div>}
    </div>
  );
}

interface Branch { id: string; name: string; address?: string }
interface Counts { total: number; vacant: number; occupied: number; reserved: number; other: number }
interface SizeStock extends Counts { size: string }
interface Inventory {
  as_of: string;
  totals: Counts & { occupancy_pct: number; branches: number };
  by_size: SizeStock[];
  pricing: Array<{ size: string; rent_incl_gst: number; deposit: number }>;
}
interface Tenant {
  tenant_id: string | null; lockerhub_application_id: string | null;
  application_no: string | null; branch_id: string | null; branch_name: string | null;
  locker_size: string | null; status: string | null; account_status: string | null; locker_no: string | null;
  tenant_name: string | null; tenant_phone: string | null; tenant_email: string | null;
  allotted_on: string | null; lease_expires_on: string | null;
  // From LockerHub's customer record — the tenant roster carries none of this.
  annual_rent?: number | string | null; deposit_amount?: number | string | null;
  lease_start?: string | null; lockers_held?: number | null; open_applications?: number | null;
  customer_id: number | null; customer_code: string | null;
  pledged_amount: number; cheque_pending: boolean; ncd_backed: boolean; unresolved: boolean;
  rent_status?: RentStatus | null;
  rent_reason?: string | null;
  rent_settlement_pending?: boolean;
  /** What LockerHub bills for the rent — the same figure the Rent Report shows. annual_rent is the price list. */
  rent_amount?: number | null;
  waiver_id: number | null; waiver_status: string | null; waiver_reason: string | null;
  linked_manually?: boolean; override_key?: string | null;
  /** The locker agreement is signed, and by which route (owner 2026-09-04). */
  agreement_signed?: boolean; agreement_method?: 'esign' | 'physical' | null;
  /** Where the agreement actually stands. `agreement_progressed` is true once a
   *  signature exists — the server decides it, so this list cannot invent its
   *  own idea of "signed" (see deposits.ts). */
  agreement_status?: string | null; agreement_label?: string | null;
  agreement_progressed?: boolean;
  agreement_signatures?: number; agreement_signers_needed?: number;
}

export function LockerTenantsPage() {
  const { promptText } = useConfirm();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [branchId, setBranchId] = useState('');
  const [q, setQ] = useState('');
  const [ncdOnly, setNcdOnly] = useState(false);
  // Part-signed is the set someone can actually DO something about today —
  // one more signature, a counter-signature, or a checker. Counting them was
  // step one; this makes the count a worklist (owner 2026-10-16).
  const [partOnly, setPartOnly] = useState(false);
  const [msg, setMsg] = useState('');

  const refetchTenants = () => qc.invalidateQueries({ queryKey: ['locker-tenants'] });
  // Link a tenant to an NCD customer by hand. LockerHub gives us no PAN to match
  // on (their profile is null for these tenants; where present the PAN is
  // masked), so an explicit human choice is the honest mechanism.
  const [linkFor, setLinkFor] = useState<Tenant | null>(null);
  const [custQ, setCustQ] = useState('');
  const custSearch = useQuery({
    queryKey: ['tenant-cust-search', custQ],
    queryFn: () => api.get<{ customers: { id: number; full_name: string; customer_code: string }[] }>(`/api/dashboard/search?q=${encodeURIComponent(custQ)}`),
    enabled: !!linkFor && custQ.trim().length >= 2,
  });
  const linkTenant = useMutation({
    mutationFn: (v: { t: Tenant; customer_id: number | null }) => api.post(`/api/lockers/tenants/${encodeURIComponent(String(v.t.override_key || v.t.tenant_id))}/link`, {
      customer_id: v.customer_id, tenant_name: v.t.tenant_name, locker_no: v.t.locker_no, branch_id: v.t.branch_id,
    }),
    onSuccess: (_r, v) => { setMsg(v.customer_id ? 'Tenant linked to the customer.' : 'Link removed.'); setLinkFor(null); setCustQ(''); refetchTenants(); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : 'Failed'),
  });
  const removeTenant = useMutation({
    mutationFn: (v: { t: Tenant; reason: string }) => api.post(`/api/lockers/tenants/${encodeURIComponent(String(v.t.override_key || v.t.tenant_id))}/remove`, {
      reason: v.reason, tenant_name: v.t.tenant_name, locker_no: v.t.locker_no, branch_id: v.t.branch_id,
    }),
    onSuccess: () => { setMsg('Removed from the NCD roster. The locker is still allotted on LockerHub — close it there too if it has actually ended.'); refetchTenants(); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : 'Failed'),
  });

  const branches = useQuery({
    queryKey: ['locker-branches'],
    queryFn: () => api.get<{ branches: Branch[] }>('/api/lockers/branches'),
  });
  // Stock comes from LockerHub's /locker-inventory (A15), NOT /locker-availability
  // (A3): A3 is a price quote for a sale and drops sizes with zero vacancy, so
  // "Extra Large sold out" used to vanish from this panel instead of being
  // stated. A15 also answers with no branch chosen, so the network total shows.
  const stock = useQuery({
    queryKey: ['locker-inventory', branchId],
    queryFn: () => api.get<Inventory>(`/api/lockers/inventory${branchId ? `?branch_id=${encodeURIComponent(branchId)}` : ''}`),
  });
  const tenants = useQuery({
    queryKey: ['locker-tenants', branchId],
    queryFn: () => api.get<{
      rows: Tenant[]; roster_complete: boolean; lockerhub_error: string | null;
      // Non-null only for a branch_staff login confined to their own branch(es)
      // (owner 2026-07-24) — the branch picker below is built from THIS, not
      // the full branch list, once it arrives.
      restricted_to: Array<{ id: string; name: string }> | null;
    }>(`/api/lockers/tenants${branchId ? `?branch_id=${encodeURIComponent(branchId)}` : ''}`),
  });

  const branchName = (id: string | null) =>
    (branches.data?.branches ?? []).find((b) => b.id === id)?.name ?? id ?? '—';

  // Which branches the picker offers. Unrestricted: every branch, as before.
  // Restricted: ONLY the caller's own — there is nothing else for them to pick,
  // and offering the full list would just invite a 403 from the backend.
  const restrictedTo = tenants.data?.restricted_to ?? null;
  const pickerBranches = restrictedTo ?? branches.data?.branches ?? [];

  const all = tenants.data?.rows ?? [];
  const rows = all.filter((r) => {
    if (ncdOnly && !r.ncd_backed) return false;
    if (partOnly && !(r.agreement_progressed && !r.agreement_signed)) return false;
    if (!q.trim()) return true;
    const hay = `${r.tenant_name ?? ''} ${r.tenant_phone ?? ''} ${r.tenant_email ?? ''} ${r.customer_code ?? ''} ${r.locker_no ?? ''} ${r.application_no ?? ''}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  // Signed agreements among the lockers currently listed. A row with no
  // LockerHub application can never have one, so it is left out of BOTH halves
  // rather than counted as unsigned.
  const agreementCount = rows.reduce(
    (acc, r) => r.lockerhub_application_id
      ? { signed: acc.signed + (r.agreement_signed ? 1 : 0),
          // Part-signed counted on its own: a locker waiting on one more
          // signature is a different job from one nobody has started.
          partial: acc.partial + (!r.agreement_signed && r.agreement_progressed ? 1 : 0),
          signable: acc.signable + 1 }
      : acc,
    { signed: 0, partial: 0, signable: 0 });
  const ncdCount = all.filter((r) => r.ncd_backed).length;
  // Counted over `all`, like ncdCount: a filter's own label must not change as
  // that filter narrows the list, or ticking it makes its number disappear.
  const partCount = all.filter((r) => r.agreement_progressed && !r.agreement_signed).length;

  return (
    <div className="w-full">
      <h1 className="text-xl font-bold tracking-tight m-0">Locker tenants</h1>
      <p className="text-sm text-text-muted mt-1 mb-4">Every locker holder, branch by branch. Pick a branch to see its occupancy and its full tenant list.</p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select className={inp} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
          {/* Restricted: no "every branch" option exists for this login — the
              blank value already means "my branch(es)" server-side. */}
          <option value="">{restrictedTo ? 'My branch' : 'All branches'}</option>
          {pickerBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <input className={`${inp} min-w-[220px]`} placeholder="Search tenant, phone, email, locker no…" value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="flex items-center gap-1.5 text-sm text-text-muted select-none">
          <input type="checkbox" checked={ncdOnly} onChange={(e) => setNcdOnly(e.target.checked)} />
          NCD-backed only ({ncdCount})
        </label>
        <label className="flex items-center gap-1.5 text-sm text-text-muted select-none"
          title="Agreements with a signature on them but not finished — waiting on a joint hirer, the authorised signatory, or a checker">
          <input type="checkbox" checked={partOnly} onChange={(e) => setPartOnly(e.target.checked)} />
          Part-signed only ({partCount})
        </label>
        {tenants.isFetching && <span className="text-xs text-text-muted">Loading…</span>}
      </div>
      {msg && <div className="text-xs text-primary mb-3">{msg}</div>}

      {/* Stock position — live from LockerHub, zeroes included. `vacant` is what
          is actually sellable; `reserved` is a locker mid-allocation and is kept
          in its own tile on purpose — it must never be counted as available. */}
      {stock.data && (
        <div className={card}>
          <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
            <h2 className="text-xs font-semibold text-text-label uppercase tracking-wide m-0">
              {branchId ? branchName(branchId) : restrictedTo ? 'My branch' : 'All branches'} · stock
            </h2>
            <span className="text-[11px] text-text-muted">
              {stock.isFetching ? 'Refreshing…' : `Live from LockerHub · ${stock.data.totals.branches} branch${stock.data.totals.branches === 1 ? '' : 'es'}`}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-3 mb-4">
            <Stat label="Total" value={stock.data.totals.total} />
            <Stat label="Vacant" value={stock.data.totals.vacant} tone="text-success" hint="Sellable right now" />
            <Stat label="Occupied" value={stock.data.totals.occupied} />
            {stock.data.totals.reserved > 0 && (
              <Stat label="Reserved" value={stock.data.totals.reserved} hint="Mid-allocation — do not promise these" />
            )}
            {stock.data.totals.other > 0 && (
              <Stat label="Other" value={stock.data.totals.other} hint="Maintenance and any status added since" />
            )}
            <Stat label="Occupancy" value={`${stock.data.totals.occupancy_pct}%`} />
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            {stock.data.by_size.map((s) => {
              const p = stock.data!.pricing.find((x) => x.size === s.size);
              const out = s.vacant === 0;
              return (
                <span key={s.size} className={`border border-border rounded px-3 py-1.5 ${out ? 'text-text-muted bg-bg' : ''}`}>
                  <b>{s.size}</b> · {out ? <span className="text-danger">none left</span> : <>{s.vacant} of {s.total} vacant</>}
                  {s.reserved > 0 && <> · {s.reserved} reserved</>}
                  {/* NCD lockers are rent-only (owner 2026-08-22) — deposit is
                      never collected, so it isn't shown. LockerHub still prices
                      one; we auto-waive it behind the scenes to allot on rent. */}
                  {p && <> · rent {formatINR(p.rent_incl_gst)}</>}
                </span>
              );
            })}
          </div>
        </div>
      )}
      {stock.isError && (
        <div className={`${card} text-xs text-text-muted`}>
          Stock position unavailable — LockerHub could not be reached. The tenant list below is unaffected.
        </div>
      )}

      {linkFor && (
        <div className="fixed inset-0 bg-black/30 flex items-start justify-center pt-24 z-20" onClick={() => setLinkFor(null)}>
          <div className="bg-surface border border-border rounded-lg shadow-card p-5 w-[460px]" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-semibold mb-1">Link “{linkFor.tenant_name}” to an NCD customer</div>
            <p className="text-xs text-text-muted mb-3">
              Automatic matching needs the phone AND the full name to agree, so a tenant recorded as
              “SEENU RAJAPPA” never matches a customer saved as “SEENU”. LockerHub gives us no PAN to
              settle it, so pick the customer yourself.
            </p>
            {linkTenant.isError && (
              <div className="text-xs text-danger mb-2">
                {linkTenant.error instanceof ApiError ? linkTenant.error.message : 'Failed to save the link.'}
              </div>
            )}
            <input className={`${inp} w-full mb-2`} autoFocus placeholder="Search name, PAN or phone…"
              value={custQ} onChange={(e) => setCustQ(e.target.value)} />
            <div className="max-h-56 overflow-auto">
              {(custSearch.data?.customers ?? []).map((c) => (
                <button key={c.id} className="block w-full text-left px-2.5 py-1.5 text-sm hover:bg-bg rounded"
                  onClick={() => linkTenant.mutate({ t: linkFor, customer_id: c.id })}>
                  {c.full_name} <span className="text-text-muted">({c.customer_code})</span>
                </button>
              ))}
              {custQ.trim().length >= 2 && (custSearch.data?.customers ?? []).length === 0 && (
                <div className="text-xs text-text-muted px-2.5 py-2">No customer matches “{custQ}”.</div>
              )}
            </div>
            <div className="flex justify-between items-center mt-3">
              {linkFor.customer_id
                ? <button className="text-xs text-danger hover:underline" onClick={() => linkTenant.mutate({ t: linkFor, customer_id: null })}>Remove existing link</button>
                : <span />}
              <button className="text-xs border border-border rounded px-3 py-1.5" onClick={() => setLinkFor(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <div className={card}>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-xs font-semibold text-text-label uppercase tracking-wide">
            Tenants {branchId ? `· ${branchName(branchId)}` : restrictedTo ? '· my branch' : '· all branches'} ({rows.length})
            {/* The same figure the Allotments page shows per series, for lockers
                (owner 2026-09-04). Counted from the rows ON SCREEN, not a
                server-side total: the branch picker and the NCD-only toggle
                filter this list, and a total computed over everything would
                disagree with the table under it. Only lockers that COULD have an
                agreement are in the denominator. */}
            {agreementCount.signable > 0 && (
              <span className={`ml-2 text-xs font-normal normal-case tracking-normal rounded px-1.5 py-0.5 ${
                agreementCount.signed === agreementCount.signable ? 'bg-[color:var(--success-bg)] text-success'
                : agreementCount.signed ? 'bg-[color:var(--warn-bg)] text-warn'
                : 'bg-[color:var(--danger-bg)] text-danger'}`}
                title="Fully signed locker agreements, by e-Sign or on paper">
                {agreementCount.signed} / {agreementCount.signable} agreements signed
              </span>
            )}
            {/* The in-between, called out rather than buried in the red count:
                these are finished bar one signature, and are the ones worth
                chasing today. */}
            {agreementCount.partial > 0 && (
              <span className="ml-2 text-xs font-normal normal-case tracking-normal rounded px-1.5 py-0.5 bg-[color:var(--warn-bg)] text-warn"
                title="Signing has started on these — waiting on a joint hirer, the authorised signatory, or a checker">
                {agreementCount.partial} part-signed
              </span>
            )}
          </h2>
        </div>

        {/* Only warn when we genuinely couldn't read the roster. A successful
            read says nothing, because nothing is missing. */}
        {tenants.data && !tenants.data.roster_complete && (
          <div className="text-xs text-warn bg-[color:var(--warn-bg)] rounded px-3 py-2 mb-3">
            <b>LockerHub roster unavailable.</b> Showing only the lockers NCD is involved in — every other tenant is missing
            from this list until their API responds again.
            {tenants.data.lockerhub_error && <span className="block mt-1 opacity-80">Last LockerHub error: {tenants.data.lockerhub_error.slice(0, 160)}</span>}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse min-w-[900px]">
            <thead>
              <tr className="text-left text-xs text-text-label uppercase tracking-wide border-b border-border">
                <th className="py-2 pr-3">Tenant</th>
                <th className="py-2 pr-3">Branch</th>
                <th className="py-2 pr-3">Locker</th>
                <th className="py-2 pr-3">Size</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3 text-right">Rent</th>
                <th className="py-2 pr-3">Rent status</th>
                <th className="py-2 pr-3">Lease</th>
                <th className="py-2 pr-3 text-right">NCD pledged</th>
                <th className="py-2 pr-3">Agreement</th>
                <th className="py-2 pr-3">Locker app</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.tenant_id || r.lockerhub_application_id || r.application_no} className="border-b border-border last:border-0">
                  <td className="py-2 pr-3">
                    {/* The name opens the complete locker profile (owner
                        2026-08-07) — the customer is linked from inside it. */}
                    {r.lockerhub_application_id
                      ? <Link to={`/app/lockers/${encodeURIComponent(r.lockerhub_application_id)}`} className="text-primary hover:underline" title="Open the complete locker profile">{r.tenant_name ?? '—'}</Link>
                      : r.customer_id
                        ? <Link to={`/app/customers/${r.customer_id}`} className="text-primary hover:underline">{r.tenant_name ?? '—'}</Link>
                        : (r.tenant_name ?? '—')}
                    {r.ncd_backed && <span className="ml-1.5 text-[10px] rounded px-1 py-0.5 bg-primary/10 text-primary align-middle">NCD</span>}
                    <div className="text-xs text-text-muted">
                      {r.tenant_phone ?? ''} {r.customer_code ? `· ${r.customer_code}` : ''}
                      {r.linked_manually && <span className="ml-1 text-[10px] text-text-muted" title="Linked by hand, not by automatic matching">(linked)</span>}
                      {can('lockers:link-tenant') && (r.override_key || r.tenant_id) && (
                        <button className="ml-1.5 text-primary hover:underline"
                          onClick={() => { setLinkFor(r); setCustQ(''); }}>{r.customer_id ? 'change link' : 'link customer…'}</button>
                      )}
                    </div>
                  </td>
                  <td className="py-2 pr-3">{r.branch_name ?? branchName(r.branch_id)}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{r.locker_no ?? '—'}</td>
                  <td className="py-2 pr-3">{r.locker_size ?? '—'}</td>
                  <td className="py-2 pr-3">
                    <span className="text-xs rounded px-1.5 py-0.5 bg-bg">{r.account_status ?? r.status ?? '—'}</span>
                    {r.cheque_pending && <span className="ml-1 text-xs rounded px-1.5 py-0.5 bg-[color:var(--warn-bg)] text-warn">cheque pending</span>}
                    {/* NCD lockers are rent-only (owner 2026-08-22): the deposit is
                        auto-waived behind the scenes so LockerHub allots on rent
                        alone, but it is never shown — no deposit, no waiver on
                        screen. The auto-waive plumbing (feeWaivers.autoWaiveDeposit)
                        and the waiver records are untouched. */}
                  </td>
                  <td className="py-2 pr-3 text-right mono" title={r.annual_rent != null ? `Price list ${formatINR(r.annual_rent)} before GST` : undefined}>
                    {(r.rent_amount ?? r.annual_rent) != null ? formatINR(r.rent_amount ?? r.annual_rent!) : '—'}
                  </td>
                  <td className="py-2 pr-3">
                    <RentStatusBadge status={r.rent_status} reason={r.rent_reason} />
                    {r.rent_reason && <div className={`text-[11px] ${r.rent_settlement_pending ? 'text-warn' : 'text-text-muted'}`}>{r.rent_settlement_pending ? '⚠ ' : ''}{r.rent_reason}</div>}
                  </td>
                  <td className="py-2 pr-3 text-xs text-text-muted whitespace-nowrap">
                    {(r.lease_start ?? r.allotted_on) ? <>{r.lease_start ?? r.allotted_on}{r.lease_expires_on ? <> → {r.lease_expires_on}</> : null}</> : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right mono">{r.pledged_amount > 0 ? formatINR(r.pledged_amount) : '—'}</td>
                  {/* Whether the agreement is signed, and by which route. Never a
                      bare "signed": the method is always named, the same rule the
                      locker profile and the enrolment card follow. A row with no
                      LockerHub application cannot have one at all, and says so
                      rather than reading as unsigned. */}
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {!r.lockerhub_application_id
                      ? <span className="text-text-muted text-xs">—</span>
                      : r.agreement_signed
                        ? <span className="text-xs rounded px-1.5 py-0.5 bg-[color:var(--success-bg)] text-success">
                            ✓ {r.agreement_method === 'physical' ? 'on paper' : 'e-Signed'}
                          </span>
                        /* Part-way is its OWN badge. Showing it as "not signed"
                           is what hid 11 lockers that need one more signature
                           and 5 waiting on the authorised signatory. */
                        : r.agreement_progressed
                          ? <span className="text-xs rounded px-1.5 py-0.5 bg-[color:var(--warn-bg)] text-warn"
                              title={r.agreement_signers_needed && r.agreement_signers_needed > 1
                                ? `${r.agreement_signatures ?? 0} of ${r.agreement_signers_needed} hirers have signed`
                                : 'Signing is under way — see the locker for what it is waiting on'}>
                              {r.agreement_label ?? 'in progress'}
                            </span>
                          : <span className="text-xs rounded px-1.5 py-0.5 bg-[color:var(--danger-bg)] text-danger"
                              title="No signature of any kind on file for this locker">not signed</span>}
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs text-text-muted">
                    {/* Re-open the application. This is the ONLY route back to a
                        tenancy once staff leave the enrolment page — and so the
                        only way to e-Sign a locker allotted earlier, or by a
                        colleague (owner 2026-08-03). */}
                    {r.lockerhub_application_id
                      ? <Link to={`/app/locker-enrollment?application_id=${encodeURIComponent(r.lockerhub_application_id)}`}
                          className="text-primary hover:underline font-sans"
                          title="Open this locker application — payments, allotment and the e-Sign agreement">
                          {r.application_no ?? r.lockerhub_application_id}
                        </Link>
                      : (r.application_no ?? '—')}
                    {can('lockers:remove-tenant') && (r.override_key || r.tenant_id) && (
                      <button className="ml-2 text-danger hover:underline font-sans"
                        title="Remove from NCD's roster. The locker stays allotted on LockerHub."
                        onClick={async () => {
                          const reason = await promptText({
                            title: `Remove ${r.tenant_name ?? 'this tenant'} from the NCD roster?`,
                            body: `Locker ${r.locker_no ?? '—'}. This hides OUR row only — the locker remains allotted on LockerHub, which owns it. Close it there too if the tenancy has actually ended.`,
                            label: 'Reason', minLength: 3, confirmLabel: 'Remove', danger: true,
                          });
                          if (reason) removeTenant.mutate({ t: r, reason });
                        }}>remove</button>
                    )}
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr><td colSpan={9} className="py-6 text-center text-text-muted">
                  {tenants.isLoading ? 'Loading…' : partOnly ? 'No part-signed agreements — every signature here is either finished or not started.' : ncdOnly ? 'No NCD-backed lockers match.' : branchId ? 'No tenants at this branch yet.' : 'No locker tenants found.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
